























import { Component, Vue, Mixins, Watch } from 'vue-property-decorator';
import { SurveysService } from '@/services/surveys.service';
import Loader from '@/components/Loader.vue';
import DynamicForm from '@/components/DynamicForm.vue';
import GroupingElement from '@/components/widgets/GroupingElement.vue';
import SurveyPageWrapper from '@/components/SurveyPageWrapper.vue';
import Logo from '@/components/Logo.vue';
import { Survey } from '@/models/survey';
import isMobile from '@/utils/isMobile';
import { DEVICE } from '@/models/enums/device.enum';
import { SurveyElementsUnion } from '@/models/survey-elements-union';
import { SurveyPayloadElement } from '@/models/survey-payload-element';
import { SurveySession } from '@/models/survey-session';
import { ANALYTICS_ACTIONS } from '@/models/enums/analytics-actions.enum';
import SessionManager from '@/helpers/session-manager';
import PageFieldsManager from '@/helpers/page-fields-manager';
import AppConfigMixin from '@/mixins/appConfigMixin';
import ErrorHandler from '@/helpers/error-handler';
import SurveyPageFooter from '@/components/SurveyPageFooter.vue';
import { LocaleMessageObject } from 'vue-i18n';
import getUserOs from '@/helpers/detect-os';
import mixpanel from 'mixpanel-browser';

@Component({
  components: {
    SurveyPageFooter,
    Loader,
    DynamicForm,
    SurveyPageWrapper,
    Logo,
    GroupingElement,
    Button: () => import('@/components/Button.vue'),
  },
})
export default class SurveyPageView extends Mixins(AppConfigMixin) {
  $refs!: Vue['$refs'] & {
    form: InstanceType<typeof DynamicForm>;
  };
  private pageFields: SurveyElementsUnion[] = [];
  private showSubmitButton: boolean = false;
  private isLoading: boolean = false;
  private isViewLoading: boolean = false;
  private surveySession: SurveySession | null = null;
  private surveySessionManager: SessionManager | null = null;
  private pageFieldsManager: PageFieldsManager | null = null;
  private isSessionEnabled: boolean = false;
  private nextButtonText: null | string = null;
  private get logoSrc(): string | null {
    return this.$store.getters.logo;
  }

  private async created() {
    this.isLoading = true;
    if (!this.checkIfSessionSlugMatches) {
      this.redirectToSurvey();
    }

    await this.setConfig();
    if (this.$store.getters.isVox) {
      document?.documentElement.style.setProperty(
        '--custom-font-name',
        'Uniform'
      );
    } else {
      document?.documentElement.style.setProperty(
        '--custom-font-name',
        'Montserrat'
      );
    }
    await Promise.all([this.setFlowConfig(), this.setTranslations()]);
    this.setDefaultColors();
    this.isLoading = false;
    await this.sendSurveyAnalytics(ANALYTICS_ACTIONS.VIEW, this.surveyId!);
  }

  mounted() {
    document.querySelector('body')!.style.overflow = isMobile()
      ? 'hidden'
      : 'auto';
  }

  private async setTranslations() {
    this.$i18n.locale = this.$store.getters.defaultLanguage.code;
    document.title = this.$t('metaTitle').toString();

    const translations = await SurveysService.getTranslations(
      this.$i18n.locale
    );

    document.documentElement.lang = this.$i18n.locale;

    this.$i18n.mergeLocaleMessage(this.$i18n.locale, {
      ...translations,
    } as LocaleMessageObject);
  }

  private hexToRgb(hex: string) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(
          result[3],
          16
        )}`
      : null;
  }

  private setDefaultColors() {
    const colors = this.$store.getters.colorsTheme;

    document.body.style.setProperty(
      '--core-color',
      this.hexToRgb(colors.primary)
    );
    document.body.style.setProperty('--bg-color', colors.bg);
  }

  private async submitHandler() {
    const isValid = await this.$refs.form.validate();
    if (!isValid) {
      return;
    }
    const pageFieldsPayload: SurveyPayloadElement[] =
      this.pageFieldsManager!.getPageFieldsPayload();

    if (!this.surveyId) return; //handle error
    try {
      this.$store.dispatch('setIsAnswerInProgress', true);
      this.isViewLoading = true;
      await SurveysService.sendAnswer(pageFieldsPayload);
      this.$store.dispatch('setIsAnswerInProgress', false);

      await this.setPageFields(() =>
        SurveysService.getNextStep(this.surveyId!)
      );
      if (this.isSessionEnabled && !this.surveySessionManager?.getSession()) {
        this.surveySessionManager?.createSession(
          this.surveyId,
          this.$store.state.config.slug,
          this.$store.state.defaultFacility.id,
          this.$route.params
        );
      }
      this.$nextTick(() => {
        this.isViewLoading = false;
      });
    } catch (error) {
      this.isViewLoading = false;
      this.$store.dispatch('setIsAnswerInProgress', false);

      ErrorHandler.handle(error);
    }
  }

  private checkIfSessionSlugMatches() {
    const { slug } = this.$store.state.config;
    const { slug: sessionSlug } = this.surveySession!;
    return slug === sessionSlug;
  }

  private deleteSession() {
    SessionManager.deleteSession();
    this.surveySession = null;
  }

  async setPageFields(getPageFieldsCallback: () => Promise<Survey>) {
    const {
      id,
      showSubmitButton,
      isNextView,
      activeViewName,
      activeBranchIdentifier,
      activeView: { elements, nextViewElement },
    } = await getPageFieldsCallback();

    this.$store.dispatch('setSurveyId', id);
    this.$store.dispatch('setActiveViewName', activeViewName);
    this.$store.dispatch('setActiveBranchIdentifier', activeBranchIdentifier);
    this.$store.dispatch('setIsNextView', isNextView);
    this.showSubmitButton = showSubmitButton;
    this.pageFieldsManager = new PageFieldsManager(
      elements,
      isNextView,
      showSubmitButton,
      id
    );
    this.pageFields = this.pageFieldsManager.getPageFields();

    this.nextButtonText = nextViewElement ? nextViewElement.text : null;

    if (this.pageFieldsManager.isSessionFinished()) {
      this.deleteSession();
    }
  }

  private redirectToSurvey() {
    this.deleteSession();

    this.$router.push({
      name: 'survey',
      params: { slug: this.$route.params.slug },
    });
  }

  private async setFlowConfig() {
    this.surveySessionManager = new SessionManager();
    this.surveySession = this.surveySessionManager.validateAndGetSessionData(
      this.$route.params
    );

    await this.setDefaultFacility(this.redirectToSurvey);

    if (this.surveySession) {
      await this.setPageFields(() =>
        SurveysService.getSurveysSession(this.surveySession!.sessionId)
      );
      return;
    }
    await this.setPageFields(() =>
      SurveysService.getSurveys({
        os: getUserOs(),
        medium: isMobile() ? DEVICE.MOBILE : DEVICE.DESKTOP,
        origin_link: this.$store.state.originLink,
        origin_gmb_address: this.$store.state.defaultFacility?.gmbAddressId,
      })
    );

    const session_id = this.$store.state.surveyId;
    const currentDistinctId = mixpanel.get_distinct_id();
    if (currentDistinctId !== session_id) {
      mixpanel.identify(session_id);
    }
  }
}
